import {
  IconCopy,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
} from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense, useState } from "react";
import { toast } from "sonner";

import { env } from "@/env/public-env";
import { useUserInfo } from "@/hooks/users/use-user-info";
import { useTRPC } from "@/infrastructure/trpc/react";
import { K8sClustersList } from "@/k8s-clusters/components/k8s-clusters-list";
import {
  RecordingRulesStatus,
  RecordingRulesStatusSkeleton,
} from "@/k8s-clusters/components/recording-rules-status";
import { Button } from "@/shared/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/shared/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/ui/collapsible";
import { Skeleton } from "@/shared/ui/skeleton";
import { OrganizationRoles } from "@vivus/common";
export const Route = createFileRoute(
  "/app/organization/$id/settings/k8s-clusters"
)({
  loader: async ({ context, params }) => {
    await context.queryClient
      .query(
        context.trpc.k8sClusters.list.queryOptions({
          organizationId: params.id,
        })
      )
      .catch(() => undefined);
  },
  component: RouteComponent,
});

const RBAC_SCRIPT_PATH = "/setup/k8s.sh";
const RECORDING_RULES_SETUP_PATH =
  "/api/recording-rules-setup/sh";

function getUrl(path: string) {
  return `${env.VITE_BASE_URL}${path}`;
}

type ScriptCardProps = {
  title: string;
  description: string;
  scriptUrl: string;
  command: string;
  scriptContent: string;
};

function ScriptCard({
  title,
  description,
  scriptUrl,
  command,
  scriptContent,
}: ScriptCardProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [copiedCommand, setCopiedCommand] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedScript, setCopiedScript] = useState(false);

  const handleCopy = async (
    text: string,
    setCopied: (v: boolean) => void,
    label: string
  ) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`${label} copied to clipboard`);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
      toast.error("Failed to copy to clipboard");
    }
  };

  return (
    <div className="space-y-4 rounded-lg border p-4">
      <div>
        <h4 className="font-medium">{title}</h4>
        <p className="mt-1 text-sm text-muted-foreground">
          {description}
        </p>
      </div>

      <div className="relative">
        <pre className="overflow-x-auto rounded-md bg-muted p-3 pr-12 font-mono text-sm break-all whitespace-pre-wrap">
          {command}
        </pre>
        <Button
          variant="ghost"
          size="icon-sm"
          className="absolute top-2 right-2"
          onClick={() =>
            handleCopy(command, setCopiedCommand, "Command")
          }
          title="Copy command"
        >
          {copiedCommand ? (
            <IconCheck className="size-4 text-success-foreground" />
          ) : (
            <IconCopy className="size-4" />
          )}
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            handleCopy(scriptUrl, setCopiedUrl, "URL")
          }
        >
          {copiedUrl ? (
            <IconCheck className="mr-2 size-4 text-success-foreground" />
          ) : (
            <IconCopy className="mr-2 size-4" />
          )}
          Copy URL
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            handleCopy(
              scriptContent,
              setCopiedScript,
              "Script"
            )
          }
        >
          {copiedScript ? (
            <IconCheck className="mr-2 size-4 text-success-foreground" />
          ) : (
            <IconCopy className="mr-2 size-4" />
          )}
          Copy Script
        </Button>
      </div>

      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger variant="accent">
          <span>View script content</span>
          {isOpen ? (
            <IconChevronUp className="size-4" />
          ) : (
            <IconChevronDown className="size-4" />
          )}
        </CollapsibleTrigger>
        <CollapsibleContent>
          <pre className="mt-2 max-h-64 overflow-x-auto overflow-y-auto rounded-md bg-muted p-3 font-mono text-xs">
            {scriptContent}
          </pre>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

const RBAC_SCRIPT = `#!/bin/bash
# RBAC Setup for vivus
# Creates read-only service account with necessary permissions

set -euo pipefail

CONTEXT="\${1:-}"
if [[ -z "$CONTEXT" ]]; then
  echo "Usage: ./vivus-setup.sh --context <context-name>"
  exit 1
fi

kubectl --context "$CONTEXT" apply -f - <<'EOF'
apiVersion: v1
kind: Namespace
metadata:
  name: management
---
apiVersion: v1
kind: ServiceAccount
metadata:
  name: vivus-readonly
  namespace: management
---
apiVersion: rbac.authorization.k8s.io/v1
kind: ClusterRoleBinding
metadata:
  name: vivus-readonly-view
roleRef:
  apiGroup: rbac.authorization.k8s.io
  kind: ClusterRole
  name: view
subjects:
  - kind: ServiceAccount
    name: vivus-readonly
    namespace: management
EOF

echo "✓ RBAC setup complete"
echo ""
echo "Get your credentials:"
echo "  Cluster URL: kubectl config view --minify -o jsonpath='{.clusters[0].cluster.server}'"
echo "  CA Cert: kubectl config view --raw --minify -o jsonpath='{.clusters[0].cluster.certificate-authority-data}' | base64 -d"
echo "  Token: kubectl -n management create token vivus-readonly --duration=87600h"`;

function getRecordingRulesUsage() {
  const setupScriptUrl = getUrl(RECORDING_RULES_SETUP_PATH);
  return `# Quick setup - auto-detects namespace and applies rules
curl -sSL ${setupScriptUrl} | bash

# Or with custom namespace:
curl -sSL ${setupScriptUrl} | bash -s -- --namespace monitoring

# Or with custom release label (for Prometheus Operator):
curl -sSL ${setupScriptUrl} | bash -s -- --namespace monitoring --release prometheus-stack

# Dry run (preview only):
curl -sSL ${setupScriptUrl} | bash -s -- --dry-run`;
}

function ScriptsSection({
  organizationId,
}: {
  organizationId: string;
}) {
  const trpc = useTRPC();

  const { data: orgsData } = useSuspenseQuery(
    trpc.organization.getOrganizationsDetails.queryOptions()
  );

  const currentOrg = orgsData?.find(
    (org) => org.organization.id === organizationId
  );
  const hasProjects =
    (currentOrg?.projects?.length ?? 0) > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>📜 Scripts</CardTitle>
        <CardDescription>
          Setup scripts for Kubernetes clusters
        </CardDescription>
      </CardHeader>
      <CardContent variant="space4">
        <ScriptCard
          title="RBAC Setup"
          description="Create read-only service account for vivus (required for cluster connection)"
          scriptUrl={getUrl(RBAC_SCRIPT_PATH)}
          command={`curl -fsSL ${getUrl(RBAC_SCRIPT_PATH)} -o vivus-setup.sh && chmod +x vivus-setup.sh && ./vivus-setup.sh --context YOUR_CONTEXT`}
          scriptContent={RBAC_SCRIPT}
        />

        <ScriptCard
          title="Recording Rules Setup"
          description="Pre-compute expensive Prometheus queries (~25s → <100ms)"
          scriptUrl={getUrl(RECORDING_RULES_SETUP_PATH)}
          command={`curl -sSL ${getUrl(RECORDING_RULES_SETUP_PATH)} | bash`}
          scriptContent={getRecordingRulesUsage()}
        />

        {!hasProjects && (
          <div className="rounded-md bg-muted p-4 text-sm text-muted-foreground">
            Create a project to view cluster readiness
            status
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RouteComponent() {
  const { id } = Route.useParams();
  const trpc = useTRPC();

  const { data: members } = useSuspenseQuery(
    trpc.organizationMember.getMembersByOrganizationId.queryOptions(
      { organizationId: id }
    )
  );
  const { data: userInfo } = useUserInfo();
  const currentMember = members?.find(
    (m) => m.id === userInfo?.id
  );
  const canManage =
    currentMember?.role === OrganizationRoles.Owner ||
    currentMember?.role === OrganizationRoles.Admin;

  return (
    <div className="space-y-6">
      <Suspense
        fallback={
          <div className="space-y-4">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-32 w-full" />
          </div>
        }
      >
        <K8sClustersList
          organizationId={id}
          canManage={canManage}
        />
      </Suspense>

      <Suspense fallback={<RecordingRulesStatusSkeleton />}>
        <RecordingRulesStatus organizationId={id} />
      </Suspense>

      <Suspense
        fallback={
          <div className="space-y-4">
            <Skeleton className="h-48 w-full" />
          </div>
        }
      >
        <ScriptsSection organizationId={id} />
      </Suspense>
    </div>
  );
}
